import React, { useState } from 'react';
import { Search, Trophy, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { mockUsers } from '@/data/mock';

const TIER_COLORS = {
  'Starter': 'bg-slate-100 text-slate-700',
  'Trader': 'bg-blue-100 text-blue-700',
  'Pro Trader': 'bg-purple-100 text-purple-700',
  'Elite Trader': 'bg-amber-100 text-amber-700',
};

export const LeaderboardPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const leaderboard = [...mockUsers]
    .sort((a, b) => b.points - a.points)
    .map((user, index) => ({
      ...user,
      rank: index + 1,
      trend: index % 3 === 0 ? 'up' : index % 3 === 1 ? 'down' : 'same',
      level: user.tier,
    }));

  const filteredLeaderboard = leaderboard.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-amber-500" />;
    if (rank === 2) return <Trophy className="h-5 w-5 text-slate-400" />;
    if (rank === 3) return <Trophy className="h-5 w-5 text-orange-600" />;
    return <span className="text-sm font-semibold text-slate-600">#{rank}</span>;
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-emerald-500" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-slate-400" />;
  };

  return (
    <div>
      <PageHeader
        title="Leaderboard"
        description="Manage trader rankings and leaderboard visibility"
        breadcrumbs={['Admin', 'Community', 'Leaderboard']}
        actions={
          <Button variant="outline">
            Recalculate Ranks
          </Button>
        }
      />

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search traders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-20">Rank</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Trader</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tier</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Points</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">Reviews</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">Payouts</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">Trend</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">Visible</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeaderboard.map((user) => (
                  <TableRow key={user.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center justify-center">
                        {getRankIcon(user.rank)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={user.avatar} />
                          <AvatarFallback>{user.name[0]}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={TIER_COLORS[user.tier]}>
                        {user.tier}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm font-bold text-slate-900">
                        {user.points.toLocaleString()}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm text-slate-600">{user.reviewCount}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm text-slate-600">{user.verifiedPayouts}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      {getTrendIcon(user.trend)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch defaultChecked />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};